/**
 * WHAT A PUPIL MAY BE OFFERED IN THE UI.
 *
 * A pupil holds real write powers — they list and amend hearings, create and edit documents on their
 * own matters, and keep tasks. These they do NOT hold, and the server refuses each one:
 *
 *   | Control                     | Server gate                                              |
 *   |-----------------------------|----------------------------------------------------------|
 *   | hearing OUTCOME fields      | `assertPupilMayNotSetOutcome` (hearings.service.ts) → 403 |
 *   | document delete / archive   | `document.delete` not granted to pupil → 403             |
 *   | diary create / edit / delete| calendar.routes.ts `WRITE_ROLES` excludes pupil → 403    |
 *   | fee / time-entry mutation   | fees + activities `WRITE_ROLES` exclude pupil → 403      |
 *   | matter create               | `case.create` not granted to pupil → 403                 |
 *   | matter status / lifecycle   | `case.update.team` / `.any` not granted → 403            |
 *   | contact create / edit       | `contact.create` / `contact.update.any` not granted → 403 |
 *   | note write                  | notes.routes.ts filters pupil out of `NOTES_WRITE_ROLES`  |
 *   | judgment create             | `judgment.create` not granted to pupil → 403             |
 *   | own-clerk capabilities      | `/me/clerk-access` `OWNER_ROLES` is silk + junior → 403   |
 *   | allocate received funds     | payments.routes.ts READ_ROLES + WRITE_ROLES exclude pupil |
 *
 * The line this module draws: it decides whether a CONTROL is drawn. It does NOT decide whether a
 * refused READ is reported. Where a pupil legitimately uses a page but one figure on it is withheld
 * — the fee-note stats / aged-debt tiles, the matter payments table — the page must report the
 * REFUSAL it actually received, so those call sites branch on the 403 rather than on member type.
 * Hiding those silently would conceal that the information exists at all. An action panel that
 * shows nothing without data is the other case, and belongs here: see `canAllocateFunds`.
 *
 * Before this module each of those controls was drawn for a pupil and then failed on submit. That is
 * the dead-end Rule #8 forbids, and it is the same reasoning already written out at
 * MatterDetailPage.tsx's void gates: "Showing a control the server will refuse is a dead-end;
 * hiding one the server allows removes a correction path the role is entitled to."
 *
 * These predicates decide only whether a control is DRAWN. The server remains authoritative, and
 * every one of these boundaries is enforced and tested on the API side
 * (`apps/api/src/lib/pupil-access-boundary.guard.test.ts`,
 * `apps/api/src/modules/hearings/hearings.pupil-outcome.test.ts`).
 *
 * Scope note: each predicate names ONLY pupil. No other member type's behaviour is changed by this
 * module — `door_tenant`, clerks and admins keep whatever the call site already decided for them.
 * Where an existing gate is imprecise for another role, that is left alone deliberately.
 *
 * This is a member-type branch in React, which the Settings feature's no-role-inference guard
 * rightly forbids for itself. Settings has a server-sent manifest to read instead; hearings,
 * documents, the diary and fees do not. Until they do, mirroring the route here — named, commented
 * and pinned by a test — is better than a control that 403s.
 */

/** Is this caller a pupil? The single place the literal appears. */
function isPupil(memberType: string | null | undefined): boolean {
  return memberType === 'pupil';
}

/**
 * May this member type record or amend a hearing OUTCOME — the court's result?
 *
 * Pupils may still edit every operational/listing field (date, duration, court, courtroom, judge,
 * hearing type, video link) and their own working notes. Only the result is withheld: `outcomeType`
 * maps onto the MATTER's status, can close or reopen it, and is posted to the instructing solicitor.
 */
export function canRecordHearingOutcome(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/** May this member type delete (soft-delete / archive) a document? */
export function canDeleteDocument(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/** May this member type create, edit or delete diary entries? Pupils observe under supervision. */
export function canWriteDiary(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/** May this member type create or amend fee records, time entries and expenses? */
export function canMutateFinancials(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/**
 * May this member type open a new matter? (`case.create`; `POST /matters` → 403 for a pupil.)
 *
 * Gates the onboarding checklist's "Start your first instruction → Add a matter" step, which sent
 * a pupil to a form whose submit could never succeed.
 */
export function canCreateMatter(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/**
 * May this member type create or edit a contact? (`contact.create` / `contact.update.any`;
 * both answer 403 for a pupil.)
 *
 * Contacts are READ-ONLY for a pupil per the ratified matrix. Contact *delete* is already gated
 * separately by the RBAC key itself (`contact.delete`) at its call site — this covers the create
 * and edit affordances, which were not.
 */
export function canMutateContacts(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/** May this member type record a judgment? (`judgment.create`; a pupil holds read only.) */
export function canCreateJudgment(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/**
 * May this member type manage their own clerks' capabilities? (`/me/clerk-access` →
 * `OWNER_ROLES = ['silk','junior']`, so a pupil gets 403.)
 *
 * Gates the "My Clerks" nav item. NOTE this subtracts ONLY pupil, so it deliberately does not
 * mirror `OWNER_ROLES` exactly: `door_tenant` also gets 403 there and keeps a dead link. That is
 * pre-existing and out of scope for the pupil work — fixing it would change another role's UI.
 */
export function canManageOwnClerks(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/**
 * May this member type change a matter — its status, or its lifecycle (close / archive / restore)?
 *
 * A pupil holds `case.read.team` and no write key at all (`seed.ts`), so `PATCH /matters/:id`,
 * `POST /:id/close`, `/archive` and `/restore` all answer 403. Gates the pipeline board's
 * drag-and-drop (which PATCHes `status`) and the matter header's lifecycle buttons.
 *
 * The pipeline case mattered most: `mattersStore.updateMatter` is optimistic and announces
 * "moved to <stage>" before the server replies, so a pupil was shown a success, then the refusal
 * rolled the card back under "please try again" — a permission decision dressed as a transient
 * fault. Not drawing the affordance is the only honest fix; the server is unchanged.
 */
export function canUpdateMatter(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/**
 * May this member type write a matter note? (`note.create` / `note.update.own` / `note.delete.own`.)
 *
 * Notes are barrister-only in the first place (`notes.routes.ts` `NOTES_ROLES`), and that part is
 * NOT this module's business — a call site still has to establish it. What this adds is the pupil
 * subtraction the server performs on top:
 *
 *     NOTES_WRITE_ROLES = NOTES_ROLES.filter((r) => r !== 'pupil')   // notes.routes.ts:24
 *
 * So a call site reads `NOTE_WRITE_TYPES.includes(mt) && canMutateNotes(mt)`, which mirrors those
 * two server lines in the same order. Keeping the barrister allowlist at the call site is
 * deliberate: a predicate here must only ever subtract a pupil, never decide another role's
 * access — see the module header and this module's own test.
 */
export function canMutateNotes(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/**
 * May this member type allocate received funds to fee notes?
 *
 * The dashboard's "Funds to allocate" panel is pure payments work: it reads
 * `GET /payments/unallocated` and each row opens `POST /payments/:id/allocate`. A pupil is in
 * NEITHER `READ_ROLES` nor `WRITE_ROLES` on payments.routes.ts, so both answer 403.
 *
 * This is a CONTROL, not a withheld figure, which is why it belongs in this module while the
 * matter Fees tab's payments table does not: that table displays money the pupil is otherwise
 * reading and must say when a row is refused, whereas this panel renders `null` without data —
 * so the only things the 403 produced were a dead request on every dashboard load and an
 * allocation affordance that could never complete.
 *
 * NOTE this subtracts ONLY pupil, so it does not mirror payments `READ_ROLES` exactly:
 * `door_tenant` is also refused there and keeps its dead request. That is pre-existing, and
 * fixing it would change another role's dashboard — same reasoning as `canManageOwnClerks`.
 */
export function canAllocateFunds(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

// ─── APPROVED-SCOPE NAVIGATION ───────────────────────────────────────────────────────────────
//
// EVERYTHING ABOVE MIRRORS A 403. THESE THREE DO NOT — and the difference matters.
//
// The predicates above exist because the server refuses the pupil, so drawing the control would be
// a dead end. The three below withhold navigation to areas the server WOULD serve: verified live on
// the dev stack 2026-10-01, a pupil gets 200 from `GET /judgments` and `GET /me/policies/pending/
// count`, and the pipeline board is an ordinary `case.read` list. They are hidden because they are
// not among the twelve ratified Pupil capabilities, which is a PRODUCT decision, not a security one.
//
// They live here anyway so there is ONE place a reader finds every pupil UI subtraction, rather than
// three ad-hoc `memberType === 'pupil'` checks scattered through the nav. But do not read them as
// security: removing one of these re-exposes a nav link, it does not grant access. Removing one of
// the predicates above re-exposes a control that 403s.
//
// Ratified list: docs/01-DOMAIN/04-ROLES-AND-PERMISSIONS.md §7.1.

/**
 * May this member type use the matter PIPELINE board? Not a pupil capability.
 *
 * The board itself is a `case.read` list the server serves happily; what it exists FOR is moving a
 * matter between stages, which PATCHes `status` and 403s for a pupil (see `canUpdateMatter`). A
 * read-only kanban of two matters is not a capability the pupil list asks for, so the nav item goes.
 */
export function canUseMatterPipeline(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/**
 * May this member type use the JUDGMENTS area? Not a pupil capability.
 *
 * NOTE the server still grants `judgment.read.any` and answers 200 — this hides the nav item only.
 * Judgment *creation* is separately refused (`canCreateJudgment`), which is the 403-mirroring gate.
 */
export function canUseJudgments(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}

/**
 * May this member type use the CHAMBERS POLICIES area? Not a pupil capability.
 *
 * NOTE the server still serves a pupil here (policy read + acknowledgement are open to every
 * member); this hides the nav item only. Governance acknowledgement is not on the pupil list.
 */
export function canUseChambersPolicies(memberType: string | null | undefined): boolean {
  return !isPupil(memberType);
}
